import { arrayValueFromQueryString } from "@octopusdeploy/design-system-octopus-components";
import { isEqual } from "lodash";
import * as React from "react";
import { QueryStringFilters } from "~/components/QueryStringFilters/QueryStringFilters";
import { useRequiredContext } from "~/hooks";
import type { ConsumerProcessTemplateEditorTabs } from "../../ProcessTemplates/ProcessTemplateStepDetails";
import type { ProcessFilter, ProcessQuery } from "../../types";
interface ProcessQueryStringContextLookupState {
    queryFilter: ProcessFilter;
}
export type ProcessQueryStringContextProps = {
    state: ProcessQueryStringContextLookupState;
    actions: ProcessQueryStringContextProviderSetupActions;
};
export const ProcessQueryStringContext = React.createContext<ProcessQueryStringContextProps | undefined>(undefined);
export const useProcessQueryStringContext = () => {
    return useRequiredContext(ProcessQueryStringContext, "ProcessQueryString");
};
export const useOptionalProcessQueryStringContext = () => {
    return React.useContext(ProcessQueryStringContext);
};
const useProcessState = (initialQueryFilter: ProcessFilter) => {
    return React.useState<ProcessQueryStringContextLookupState>({
        queryFilter: initialQueryFilter,
    });
};
const getStateUpdaters = (setState: React.Dispatch<React.SetStateAction<ProcessQueryStringContextLookupState>>) => {
    return {
        onQueryFilterChange: (callback: (prev: ProcessFilter) => ProcessFilter) => setState((current) => ({ ...current, queryFilter: callback(current.queryFilter) })),
    };
};
export interface ProcessQueryStringContextProviderSetup {
    state: ProcessQueryStringContextLookupState;
    setState: React.Dispatch<React.SetStateAction<ProcessQueryStringContextLookupState>>;
    actions: ProcessQueryStringContextProviderSetupActions;
}
export interface ProcessQueryStringContextProviderSetupActions {
    onQueryFilterChange: (callback: (prev: ProcessFilter) => ProcessFilter) => void;
    showEmptyStepEditor: () => void;
    showProcessStepTemplates: () => void;
    addProcessTemplateStep: (processTemplateSlug: string, processTemplateVersionMask: string) => void;
    showProcessAction: (actionId: string) => void;
    showProcessParentStep: (parentStepId: string) => void;
    changeProcessTemplateEditorActiveTab: (activeTab: ConsumerProcessTemplateEditorTabs) => void;
}
interface ProcessQueryStringControllerProps {
    initialQueryFilter: ProcessFilter;
    children: (renderProps: ProcessQueryStringContextProps) => React.ReactNode;
}
const ProcessQueryStringFilters = QueryStringFilters<ProcessFilter, ProcessQuery>;
export const ProcessQueryStringController: React.FC<ProcessQueryStringControllerProps> = ({ children, initialQueryFilter }) => {
    const [lookupsState, setState] = useProcessState(initialQueryFilter);
    const stateUpdaters = React.useMemo(() => getStateUpdaters(setState), [setState]);
    const setFilter = (overrides?: Partial<ProcessFilter>) => {
        const filter = { ...createDefaultFilter(), ...overrides };
        stateUpdaters.onQueryFilterChange((prev) => ({ ...prev, ...filter }));
    };
    const showEmptyStepEditor = () => {
        setFilter();
    };
    const showProcessStepTemplates = () => {
        setFilter({ stepTemplates: "1" });
    };
    const addProcessTemplateStep = (processTemplateSlug: string, processTemplateVersionMask: string) => {
        setFilter({
            new: "true",
            actionType: "Octopus.ProcessTemplate",
            processTemplateSlug,
            processTemplateVersionMask,
        });
    };
    const showProcessAction = (actionId: string) => {
        setFilter({ actionId });
    };
    const showProcessParentStep = (parentStepId: string) => {
        setFilter({ parentStepId });
    };
    const changeProcessTemplateEditorActiveTab = (activeTab: ConsumerProcessTemplateEditorTabs) => {
        stateUpdaters.onQueryFilterChange((prev) => ({ ...prev, activeTab }));
    };
    const contextValue: ProcessQueryStringContextProps = {
        state: lookupsState,
        actions: {
            onQueryFilterChange: stateUpdaters.onQueryFilterChange,
            showEmptyStepEditor,
            showProcessStepTemplates,
            addProcessTemplateStep,
            showProcessAction,
            showProcessParentStep,
            changeProcessTemplateEditorActiveTab,
        },
    };
    const onFilterUpdated = (filter: ProcessFilter) => {
        if (!isEqual(filter, lookupsState.queryFilter)) {
            stateUpdaters.onQueryFilterChange((prev) => ({ ...prev, ...filter }));
        }
    };
    return (<ProcessQueryStringContext.Provider value={contextValue}>
            <ProcessQueryStringFilters filter={lookupsState.queryFilter} getQuery={queryFromFilters} getFilter={getFilterFromQuery} onFilterChange={(filter) => onFilterUpdated(filter)}/>
            {children(contextValue)}
        </ProcessQueryStringContext.Provider>);
};
ProcessQueryStringController.displayName = "ProcessQueryStringController"
export const createDefaultFilter = (): ProcessFilter => {
    return {
        actionId: "",
        actionType: "",
        reloadKey: "",
        stepTemplates: "",
        childStepTemplates: "",
        templateId: "",
        templateSpaceId: "",
        parentStepId: "",
        new: "",
        actionCategory: "",
        processTemplateSlug: "",
        processTemplateVersionMask: "",
        tags: [],
        activeTab: "",
    };
};
const getFilterFromQuery = (query: ProcessQuery): ProcessFilter => {
    return {
        actionId: query.actionId || "",
        actionType: query.actionType || "",
        reloadKey: query.reloadKey || "",
        stepTemplates: query.stepTemplates || "",
        childStepTemplates: query.childStepTemplates || "",
        templateId: query.templateId || "",
        templateSpaceId: query.templateSpaceId || "",
        isCommunityTemplate: query.isCommunityTemplate,
        parentStepId: query.parentStepId || "",
        new: query.new || "",
        actionCategory: query.actionCategory || "",
        processTemplateSlug: query.processTemplateSlug || "",
        processTemplateVersionMask: query.processTemplateVersionMask || "",
        tags: arrayValueFromQueryString(query.tags),
        gitRef: query.gitRef || "",
        activeTab: query.activeTab || "",
    };
};
const queryFromFilters = (filter: ProcessFilter): ProcessQuery => {
    return {
        actionId: filter.actionId,
        actionType: filter.actionType,
        reloadKey: filter.reloadKey,
        stepTemplates: filter.stepTemplates,
        childStepTemplates: filter.childStepTemplates,
        templateId: filter.templateId,
        templateSpaceId: filter.templateSpaceId,
        isCommunityTemplate: filter.isCommunityTemplate,
        parentStepId: filter.parentStepId,
        new: filter.new,
        actionCategory: filter.actionCategory,
        processTemplateSlug: filter.processTemplateSlug,
        processTemplateVersionMask: filter.processTemplateVersionMask,
        tags: arrayValueFromQueryString(filter.tags),
        gitRef: filter.gitRef,
        activeTab: filter.activeTab,
    };
};
export interface WithProcessQueryStringContextInjectedProps {
    processQueryStringContext: ProcessQueryStringContextProps;
}
export const withProcessQueryStringContext = <T,>(Component: React.ComponentType<T & WithProcessQueryStringContextInjectedProps>) => {
    const WithProcessQueryStringContext: React.FC<T> = (props) => {
        const context = useProcessQueryStringContext();
        return <Component processQueryStringContext={context} {...props}/>;
    };
    WithProcessQueryStringContext.displayName = "WithProcessQueryStringContext"
    return WithProcessQueryStringContext;
};
